import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShoppingBag, Headphones, FileText, Video, Loader2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  pdf: { Icon: FileText, label: "PDF" },
  audio: { Icon: Headphones, label: "Audio" },
  video: { Icon: Video, label: "Video" },
};

const ProductCard = ({ p, pathKey, onBuy, busy, buyLabel }) => {
  const isMen = pathKey === "men";
  const { Icon, label } = KIND_META[p.kind] || KIND_META.pdf;

  const cardCls = isMen
    ? "bg-[#0F1014] border border-[#2C313D] hover:border-[#D4AF37]"
    : "bg-[#EBE7E0] border border-[#D5CECA] hover:bg-[#E3DDD3]";
  const headingCls = isMen ? "text-white" : "text-[#2C332E]";
  const bodyCls = isMen ? "text-[#8A91A5]" : "text-[#5C6660]";
  const priceCls = isMen ? "text-[#D4AF37]" : "text-[#738678]";
  const btn = isMen ? "btn-gold w-full justify-center" : "btn-sage w-full justify-center";

  return (
    <div data-testid={`product-${p.id}`} className={`p-7 transition rounded-2xl flex flex-col ${cardCls}`}>
      {p.cover_url ? (
        <div className="aspect-[16/10] w-full overflow-hidden rounded-xl mb-5 border border-current/10">
          <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`aspect-[16/10] w-full rounded-xl mb-5 flex items-center justify-center ${isMen ? "bg-[#1A1D24]" : "bg-[#F4F1ED]"}`}>
          <Icon size={48} className={priceCls} />
        </div>
      )}
      <div className="flex items-center justify-between text-[0.7rem] uppercase tracking-[0.22em]">
        <span className={priceCls}>{label}</span>
        {p.duration_min ? <span className={bodyCls}>{p.duration_min} min</span> : null}
      </div>
      <h3 className={`mt-3 text-xl md:text-2xl leading-snug ${headingCls}`} style={isMen ? { fontFamily: "Clash Display, Cabinet Grotesk, sans-serif", fontWeight: 600 } : { fontFamily: "Cormorant Garamond, serif" }}>{p.title}</h3>
      <p className={`mt-3 ${bodyCls} leading-relaxed text-sm flex-1`}>{p.description}</p>
      <div className="mt-5 flex items-end justify-between">
        <span className={`text-3xl ${priceCls}`} style={isMen ? { fontFamily: "Clash Display, sans-serif", fontWeight: 600 } : { fontFamily: "Cormorant Garamond, serif" }}>
          {p.price_eur.toFixed(2).replace(".", ",")} €
        </span>
      </div>
      <button
        data-testid={`product-buy-${p.id}`}
        onClick={() => onBuy(p)}
        disabled={busy}
        className={`mt-5 ${btn}`}
      >
        {busy ? <Loader2 className="animate-spin" size={16} /> : <ShoppingBag size={16} />}
        {buyLabel}
      </button>
    </div>
  );
};

const BuyModal = ({ product, pathKey, onClose, onSubmit, busy, sa }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const isMen = pathKey === "men";

  if (!product) return null;

  const overlay = isMen ? "bg-black/85" : "bg-[#2C332E]/65";
  const surface = isMen ? "bg-[#0F1014] border-[#2C313D] text-white" : "bg-[#FDFCF9] border-[#D5CECA] text-[#2C332E]";
  const field = isMen
    ? "w-full bg-transparent border-b border-[#2C313D] focus:border-[#D4AF37] outline-none py-3 text-white"
    : "w-full bg-transparent border-b border-[#C9C2BC] focus:border-[#738678] outline-none py-3 text-[#2C332E]";
  const labelCls = isMen ? "text-[0.7rem] uppercase tracking-[0.24em] text-[#8A91A5]" : "text-[0.7rem] uppercase tracking-[0.24em] text-[#5C6660]";

  return (
    <div data-testid="buy-modal" className={`fixed inset-0 ${overlay} backdrop-blur-sm z-50 flex items-center justify-center p-4`} onClick={onClose}>
      <div className={`relative max-w-md w-full border ${surface} p-8`} onClick={(e) => e.stopPropagation()}>
        <button data-testid="buy-modal-close" onClick={onClose} className="absolute right-4 top-4 text-xs uppercase tracking-[0.22em] opacity-60 hover:opacity-100">{sa.close}</button>
        <p className="eyebrow" style={{ color: isMen ? "#D4AF37" : "#738678" }}>{KIND_META[product.kind]?.label}</p>
        <h3 className="mt-3 text-2xl" style={isMen ? { fontFamily: "Clash Display, sans-serif", fontWeight: 600 } : { fontFamily: "Cormorant Garamond, serif" }}>{product.title}</h3>
        <p className="mt-2 text-3xl" style={{ color: isMen ? "#D4AF37" : "#738678", fontFamily: isMen ? "Clash Display, sans-serif" : "Cormorant Garamond, serif" }}>{product.price_eur.toFixed(2).replace(".", ",")} €</p>
        <form
          className="mt-6 space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || !email.trim()) return;
            onSubmit({ name, email });
          }}
        >
          <div>
            <label className={labelCls}>{sa.modal_name}</label>
            <input data-testid="buy-name" value={name} required minLength={2} onChange={(e) => setName(e.target.value)} className={field} />
          </div>
          <div>
            <label className={labelCls}>{sa.modal_email}</label>
            <input data-testid="buy-email" type="email" value={email} required onChange={(e) => setEmail(e.target.value)} className={field} />
          </div>
          <button data-testid="buy-submit" type="submit" disabled={busy} className={isMen ? "btn-gold w-full justify-center" : "btn-sage w-full justify-center"}>
            {busy ? <Loader2 className="animate-spin" size={16} /> : <ShoppingBag size={16} />}
            {sa.checkout}
          </button>
        </form>
        <p className={`mt-4 text-xs ${isMen ? "text-[#8A91A5]" : "text-[#5C6660]"}`}>
          {sa.modal_note}
        </p>
      </div>
    </div>
  );
};

const Shop = ({ pathKey = "women" }) => {
  const { lang, t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // product being bought
  const [busy, setBusy] = useState(false);
  const isMen = pathKey === "men";
  const sh = (pathKey === "men" ? t.men.shop : t.women.shop);
  const sa = t.shop_actions;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/products`, { params: { path: pathKey } });
        setProducts(res.data);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    })();
  }, [pathKey, lang]);

  const handleBuy = async ({ name, email }) => {
    if (!active) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        product_id: active.id,
        customer_name: name,
        customer_email: email,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || sa.checkout_failed);
      setBusy(false);
    }
  };

  if (!loading && products.length === 0) return null; // hide section entirely if no products

  return (
    <section id="shop" className={`relative px-6 md:px-12 lg:px-20 py-20 border-t ${isMen ? "border-[#2C313D]" : "border-[#D5CECA]"} overflow-hidden`}>
      <div className="max-w-3xl">
        <span className="eyebrow" style={{ color: isMen ? "#8A91A5" : "#5C6660" }}>
          {sh.eyebrow}
        </span>
        <h2 className={`mt-5 text-3xl md:text-4xl ${isMen ? "text-white" : "text-[#2C332E]"}`} style={isMen ? { fontFamily: "Clash Display, sans-serif", fontWeight: 600 } : { fontFamily: "Cormorant Garamond, serif" }}>
          {sh.title}
        </h2>
        <p className={`mt-4 ${isMen ? "text-[#8A91A5]" : "text-[#5C6660]"} text-base md:text-lg leading-relaxed`}>
          {sh.sub}
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p) => (
          <ProductCard key={p.id} p={p} pathKey={pathKey} onBuy={setActive} busy={busy && active?.id === p.id} buyLabel={sa.buy} />
        ))}
      </div>

      <BuyModal product={active} pathKey={pathKey} onClose={() => setActive(null)} onSubmit={handleBuy} busy={busy} sa={sa} />
    </section>
  );
};

export default Shop;
